import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type { ShopifyOrderView } from "../../api/types";
import { REASON_LABEL, REASON_TONE, byUrgency, leadReason, reasonDetail, toneClass } from "./webOrderAttention";

const REFRESH_MS = 30_000;

/**
 * G-160 (OD-263) — on the Orders page, for owners and managers: every web
 * order that needs someone, most urgent first, each opening its card on Web
 * orders where the fix is. Renders nothing when no order needs anyone.
 */
export function WebOrdersNeedSomeone() {
  const [orders, setOrders] = useState<ShopifyOrderView[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api
        .listShopifyOrders()
        .then((rows) => {
          if (!cancelled) setOrders(rows.filter((order) => order.attention.length > 0));
        })
        .catch(() => {
          if (!cancelled) setOrders([]);
        });
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  if (orders.length === 0) return null;
  const sorted = [...orders].sort(byUrgency);

  return (
    <section className="card wo-need" aria-labelledby="wo-need-title">
      <div className="wo-need-head">
        <h3 id="wo-need-title">Web orders that need someone · {orders.length}</h3>
        <Link to="/app/shopify-orders">Open Web orders</Link>
      </div>
      <ul className="wo-need-list">
        {sorted.map((order) => {
          const reason = leadReason(order) ?? order.attention[0];
          const blocked = REASON_TONE[reason] === "blocked";
          return (
            <li key={order.id} className={blocked ? "is-blocked" : undefined}>
              <span className={toneClass(reason)}>{REASON_LABEL[reason]}</span>
              <strong className="wo-need-number">{order.orderNumber ?? order.shopifyOrderId}</strong>
              <span className="wo-need-detail">{reasonDetail(order, reason)}</span>
              <Link
                to={`/app/shopify-orders?order=${encodeURIComponent(order.id)}`}
                className={blocked ? "button wo-need-fix" : "button plain wo-need-fix"}
              >
                {blocked ? "Fix it" : "Open"}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
